/**
 * Thin wrapper over Beacon.js (window.athos.tracker) for the events we own.
 *
 * Events we send (beacon=true on API requests does NOT send "render" for us; Athos counts a search
 * from the render event, so it must be sent explicitly each time results are shown):
 *   render       - a set of results was shown (search page, each content row, dropdown)
 *   impression   - results scrolled into view / shown in the dropdown
 *   clickThrough - a result was clicked
 *   addToCart    - quick add used on a search result
 *   redirect     - the search was redirected by a merchandising rule
 * Cart, order and product page-view events come from the Shopify Web Pixel (enabled in the Athos Console).
 *
 * Every call is a safe no-op if Beacon.js is missing (blocked by an ad blocker, etc.).
 * Docs: https://docs.athoscommerce.com/docs/library-beaconjs
 *       https://docs.athoscommerce.com/reference/search-tracking-guide
 */
(function () {
  const config = window.AthosConfig;
  if (!config) return;

  const tracker = () => (window.athos && window.athos.tracker && window.athos.tracker.events ? window.athos.tracker : null);

  // Development mode tags events as dev traffic and logs to the console.
  const t = tracker();
  if (t) {
    if (config.testMode) t.updateContext('dev', 'development');
    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (currency && t.setCurrency) t.setCurrency({ code: currency });
  }

  // siteId overrides the global one, so events for blog content go to the content account.
  const send = (scope, action, data, siteId) => {
    const instance = tracker();
    if (!instance || !instance.events[scope] || !instance.events[scope][action]) return;
    try {
      const payload = { data };
      if (siteId) payload.siteId = siteId;
      instance.events[scope][action](payload);
    } catch (error) {
      console.error(`Athos tracking ${scope}.${action} failed`, error);
    }
  };

  /** Map an Athos API result to the identifiers Beacon events require. */
  const toResult = (result, extra) =>
    Object.assign(
      {
        type: 'product',
        parentId: String(result.parentId || result.uid),
        uid: String(result.uid),
        sku: result.sku ? String(result.sku) : undefined
      },
      extra
    );

  const api = (siteId) => ({
    toResult,

    /** A set of results was shown. Counts the search itself in Athos reports (searches, popular terms). */
    render(scope, responseId) {
      if (!responseId) return;
      send(scope, 'render', { responseId }, siteId);
    },

    impression(scope, responseId, results, banners) {
      const bannerList = banners || [];
      if (!responseId || (!results.length && !bannerList.length)) return;
      send(scope, 'impression', { responseId, results: results.map((r) => toResult(r)), banners: bannerList }, siteId);
    },

    /** Pass a result for a product click, or a bannerUid for a merchandising banner click. */
    clickThrough(scope, responseId, result, bannerUid) {
      if (!responseId) return;
      if (bannerUid) send(scope, 'clickThrough', { responseId, banners: [{ uid: String(bannerUid) }] }, siteId);
      else if (result) send(scope, 'clickThrough', { responseId, results: [toResult(result)] }, siteId);
    },

    addToCart(scope, responseId, result, qty) {
      if (!responseId || !result) return;
      send(
        scope,
        'addToCart',
        { responseId, results: [toResult(result, { qty: qty || 1, price: parseFloat(result.price) || 0 })] },
        siteId
      );
    },

    redirect(scope, responseId, url) {
      if (!responseId || !url) return;
      send(scope, 'redirect', { responseId, redirect: url }, siteId);
    }
  });

  window.AthosTracking = api();
  // blog and collection results each belong to their own Athos account
  const sites = {};
  window.AthosTracking.forSite = (siteId) => (sites[siteId] = sites[siteId] || api(siteId));
})();
